<template>
  <div class="article-wrapper">
    <b-alert
      v-if="!article.published && !previewMode"
      variant="warning"
      show
      class="mb-2"
    >
      <h4 class="alert-heading">
        <i class="fas fa-info-circle"></i> Denna artikeln är ett utkast
      </h4>
      <div class="alert-body">
        <p class="mb-0">
          Just nu är det bara du som kan se den här artikeln. För att publicera
          artikeln, klicka på "Redigera" och sedan på "Spara och publicera".
        </p>
      </div>
    </b-alert>
    <div class="article">
      <b-row>
        <b-col cols="12" :lg="previewMode ? 12 : 8" class="article-main">
          <!-- main column --->
          <b-row class="article-header">
            <b-col cols="12">
              <b-card no-body class="section section-description"
                ><!-- eller article.title ?? -->
                <template #header>
                  <h6>Innehåll i korthet</h6>
                </template>
                <div class="card-body">
                  <b-card-text class="article-description">
                    <h1 class="mb-1">{{ article.title }}</h1>
                    <div
                      v-if="articleFeatureImage"
                      class="backdrop"
                      :style="
                        'background-image: url(' +
                          articleFeatureImage.small +
                          ');'
                      "
                    >
                      <figure v-if="article.feature_image" class="text-center">
                        <picture>
                          <source
                            media="(min-width:768px)"
                            :srcset="articleFeatureImage.large"
                          />
                          <source
                            media="(min-width:776px)"
                            :srcset="articleFeatureImage.medium"
                          />
                          <source
                            media="(max-width:775px)"
                            :srcset="articleFeatureImage.small"
                          />
                          <img
                            :src="articleFeatureImage.original"
                            :alt="article.feature_image.fileName"
                          />
                        </picture>
                      </figure>
                    </div>
                    <div
                      v-html="article.description"
                      class="article-description-text"
                    ></div>
                  </b-card-text>
                </div>
              </b-card>
            </b-col>
          </b-row>
          <div class="sections">
            <Section
              v-for="section in article.sections"
              :editMode="false"
              :section="section"
              :key="section.id"
            />
          </div>
        </b-col>
        <b-col v-if="!previewMode" cols="12" lg="4" class="article-sidebar"
          ><!-- Sidebar on large screens -->
          <b-row>
            <b-col cols="12">
              <b-card no-body class="section section-meta-data">
                <template #header>
                  <h6 class="mb-0">Om artikeln</h6>
                  <b-button
                    v-b-toggle="'collapse-meta-data'"
                    variant="link"
                    size="sm"
                    ><i class="fas fa-chevron-down"></i
                  ></b-button>
                </template>
                <b-collapse visible id="collapse-meta-data">
                  <div class="card-body">
                    <b-card-text>
                      <h6>Läst av:</h6>
                      <!-- Här behöver vi hämta information från users och loopa igenom -->
                      <b-avatar-group>
                        <b-avatar
                          v-for="(contact, index) in readByContacts.filter(
                            (c) => c.id && c.avatar
                          )"
                          :key="contact.id"
                          v-b-tooltip.hover
                          class="pull-up"
                          :title="contact.first_name + ' ' + contact.last_name"
                          :src="contactImageUrls[index]"
                        />
                        <b-avatar
                          v-for="contact in readByContacts.filter(
                            (c) => c.id && !c.avatar
                          )"
                          :key="contact.id"
                          v-b-tooltip.hover
                          class="pull-up"
                          :title="contact.first_name + ' ' + contact.last_name"
                          :text="contact.firstLetter + contact.secondLetter"
                        />
                      </b-avatar-group>
                    </b-card-text>
                    <b-card-text v-if="author">
                      <h6>Skapad {{ formatDate(article.created) }} av:</h6>
                      <b-avatar
                        v-if="author.avatar"
                        size="md"
                        v-b-tooltip.hover
                        class="pull-up"
                        :title="author.first_name + ' ' + author.last_name"
                        :src="authorImage"
                      />
                      <b-avatar
                        v-else
                        size="md"
                        v-b-tooltip.hover
                        class="pull-up"
                        :title="author.first_name + ' ' + author.last_name"
                        :text="author.first_name[0] + author.last_name[0]"
                      />
                      {{ article.created_by }}
                      <small
                        >{{ author.first_name }} {{ author.last_name }}</small
                      >
                    </b-card-text>
                    <b-card-text v-if="updated_by">
                      <h6>
                        Senast uppdaterad
                        {{ formatDate(article.last_updated) }} av:
                      </h6>
                      <b-avatar
                        v-if="updated_by.avatar"
                        size="md"
                        v-b-tooltip.hover
                        class="pull-up"
                        :title="
                          updated_by.first_name + ' ' + updated_by.last_name
                        "
                        :src="updatedByImage"
                      />
                      <b-avatar
                        v-else
                        size="md"
                        v-b-tooltip.hover
                        class="pull-up"
                        :title="
                          updated_by.first_name + ' ' + updated_by.last_name
                        "
                        :text="
                          updated_by.first_name[0] + updated_by.last_name[0]
                        "
                      />
                      <small>
                        {{ updated_by.first_name }}
                        {{ updated_by.last_name }}</small
                      >
                    </b-card-text>
                    <b-card-text
                      v-if="!article.is_news_article && !article.is_about_me"
                    >
                      <p>
                        <strong>Kategorier:</strong
                        ><br /><!-- Här behöver vi hämta namnen på kategorierna och loopa ut dem istället -->
                        <span
                          v-for="(category, index) in article.categories"
                          :key="index"
                        >
                          {{ getArticleCategoryById(category).title }},
                        </span>
                      </p>
                    </b-card-text>
                    <b-card-text>
                      <h6>Sökord:</h6>
                      <!-- Här behöver vi hämta namnen på taggarna och loopa ut dem istället -->
                      <span v-for="tag in article.tags" :key="tag.id">
                        {{ tag }},
                      </span>
                    </b-card-text>
                    <!-- <b-tabs> 
                                                <b-tab title="Info"> 
                                                    
                                                </b-tab> -->
                    <!-- Tab 2: Feedback
                                                <b-tab>
                                                    <template #title>
                                                        Feedback <b-badge pill variant="warning" size="sm">2</b-badge>
                                                    </template>
                                                    <b-card-text>
                                                        <app-timeline>
                                                                <app-timeline-item variant="warning">
                                                                    <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0">
                                                                        <h6>Rubrik 2</h6>
                                                                        <small class="text-muted">2021-08-12</small>
                                                                    </div>
                                                                    <p><small>Filmen är skakig jag kan inte se hur jag ska göra inställninge. Kan du spela in en ny film?</small></p>
                                                                    <p>
                                                                        <b-avatar
                                                                        size="md"
                                                                        :src="require('@/assets/images/portrait/small/avatar-s-5.jpg')"
                                                                        />
                                                                        user.name <small>(Stödperson)</small>
                                                                    </p>
                                                                    <b-button variant="success" size="sm" class="mr-1">Åtgärdad <i class="fas fa-check"></i></b-button>
                                                                    <b-button variant="danger" size="sm">Ta bort <i class="fas fa-trash"></i></b-button>
                                                                </app-timeline-item>
                                                                <app-timeline-item variant="warning">
                                                                    <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0">
                                                                        <h6>Rubrik 1</h6>
                                                                        <small class="text-muted">2021-07-14</small>
                                                                    </div>
                                                                    <div class="feedback-comment">
                                                                        <p><small>Filmen är skakig jag kan inte se hur jag ska göra inställninge. Kan du spela in en ny film?</small></p>
                                                                    </div>
                                                                    <p>
                                                                        <b-avatar
                                                                        size="md"
                                                                        :src="require('@/assets/images/portrait/small/avatar-s-3.jpg')"
                                                                        />
                                                                        user.name <small>(Stödperson)</small>
                                                                    </p>
                                                                    <b-button variant="success" size="sm" class="mr-1">Åtgärdad <i class="fas fa-check"></i></b-button>
                                                                    <b-button variant="danger" size="sm">Ta bort <i class="fas fa-trash"></i></b-button>
                                                                </app-timeline-item>
                                                        </app-timeline>
                                                    </b-card-text>
                                                    <b-card-text>
                                                        <h6>Ge feedback:</h6>
                                                        <b-form  @submit.prevent>
                                                            <b-form-group>
                                                                <b-form-select
                                                                    v-model="feedBackSelected"
                                                                    :options="feedBackOptions" 
                                                                >
                                                                </b-form-select>
                                                            </b-form-group>
                                                            <div class="form-label-group">
                                                                <b-form-textarea
                                                                id="textarea"
                                                                rows="3"
                                                                placeholder="Skriv en kommentar som hjälper till att förbättra artikeln."
                                                                />
                                                                <label for="label-textarea">Skriv en kommentar som hjälper till att förbättra artikeln.</label>
                                                            </div>
                                                            <b-form-group>
                                                                <b-button variant="success" block>Skicka feedback</b-button>
                                                            </b-form-group>
                                                        </b-form>
                                                    </b-card-text>
                                                </b-tab> -->
                    <!-- 
                                                    Logg över händelser för ensklid artikel
                                                    Vi avaktar med denna delen
                                                
                                                <b-tab title="Logg">
                                                    <b-card-text>
                                                        <app-timeline>
                                                                <app-timeline-item variant="success">
                                                                    <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0">
                                                                        <h6>{user.name] har uppdaterat artikeln</h6>
                                                                        <small class="text-muted">2012-04-23</small>
                                                                    </div>
                                                                    <p>
                                                                        <b-avatar
                                                                        size="md"
                                                                        :src="require('@/assets/images/portrait/small/avatar-s-5.jpg')"
                                                                        />
                                                                        user.name <small>(Stödperson)</small>
                                                                    </p>
                                                                </app-timeline-item>
                                                                <app-timeline-item variant="success">
                                                                    <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0">
                                                                        <h6>{user.name] har uppdaterat artikeln</h6>
                                                                        <small class="text-muted">2012-04-23</small>
                                                                    </div>
                                                                    <p>
                                                                        <b-avatar
                                                                        size="md"
                                                                        :src="require('@/assets/images/portrait/small/avatar-s-3.jpg')"
                                                                        />
                                                                        user.name <small>(Stödperson)</small>
                                                                    </p>
                                                                </app-timeline-item>
                                                                <app-timeline-item variant="success">
                                                                    <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0">
                                                                        <h6>{user.name] har skapat artikeln</h6>
                                                                        <small class="text-muted">2012-04-23</small>
                                                                    </div>
                                                                    <p>
                                                                        <b-avatar
                                                                        size="md"
                                                                        :src="require('@/assets/images/portrait/small/avatar-s-5.jpg')"
                                                                        />
                                                                        user.name <small>(Stödperson)</small>
                                                                    </p>
                                                                </app-timeline-item>
                                                        </app-timeline>
                                                    </b-card-text>
                                                </b-tab> -->
                    <!-- </b-tabs> -->
                  </div>
                  <!-- end card-body -->
                </b-collapse>
              </b-card>
            </b-col>
          </b-row>
          <b-row class="article-actions mb-3">
            <b-col cols="12">
              <b-button
                v-if="!previewMode"
                v-can-write:[isArticleOwner]="$permissions.WRITE_OTHER_ARTICLE"
                @click="editArticle"
                variant="primary"
                size="lg"
                block
                >Redigera <i class="fas fa-edit"></i
              ></b-button>
              <b-button
                v-if="
                  (!previewMode && article.favorite == false) ||
                    isFavorite == false
                "
                v-can-write:[isArticleOwner]="$permissionGroup.User.Permissions"
                @click="setFavorite"
                variant="primary"
                size="lg"
                block
                >Gör till favorit <i class="fas fa-star"></i
              ></b-button>
              <b-button
                v-if="
                  (!previewMode && article.favorite == true) ||
                    isFavorite == true
                "
                v-can-write:[isArticleOwner]="$permissionGroup.User.Permissions"
                @click="unsetFavorite"
                variant="warning"
                size="lg"
                block
                >Ta bort som favorit <i class="fas fa-star"></i
              ></b-button>
              <b-button
                v-if="!previewMode"
                v-can-delete:[isArticleOwner]="$permissions.WRITE_OTHER_ARTICLE"
                @click="deleteArticleAlert"
                variant="danger"
                size="lg"
                block
                >Radera <i class="fas fa-trash"></i
              ></b-button>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCollapse,
  BCardText,
  BButton,
  BAlert,
  // BTabs, BTab, BBadge, BForm, BFormGroup, BFormSelect, BFormTextarea,
} from "bootstrap-vue";
import { BAvatarGroup, BAvatar, VBTooltip } from "bootstrap-vue";
import moment from "moment";
//import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
//import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import Section from "@/components/Article/Section.vue";
import { mapGetters, mapActions } from "vuex";
import Article from "@/models/Article";
import permissions from "@/UserPermissions/v-can_Directive";
import axios from "axios";

export default {
  props: ["articleId", "previewMode"],
  data() {
    return {
      feedBackSelected: null,
      feedBackOptions: [
        { value: null, text: "Välj rubrik" },
        { value: "Rubrik 1", text: "Rubrik 1" },
        { value: "Rubrik 2", text: "Rubrik 2" },
        { value: "Rubrik 3", text: "Rubrik 3" },
      ],
      isFavorite: null,
      isArticleOwner: true,
      read_by_contacts: [],
      author: {},
      updated_by: undefined,
      articleFeatureImage: {
        original: null,
        small: null,
        medium: null,
        large: null,
      },
      contactImageUrls: [],
      authorImage: null,
      updatedByImage: null,
    };
  },
  async created() {
    const article = this.getArticleById(this.articleId);
    const ownerId = article.owner.id ?? article.owner;
    this.isArticleOwner = this.$currentUserId() == ownerId;
    this.author = this.getContactByUserId(ownerId);

    if (article.last_updated_by) {
      const id = article.last_updated_by.id ?? article.last_updated_by;
      this.updated_by = this.getContactByUserId(id);
    }

    let articleSecurityGroups = 0;
    if (article.security_groups.length > 0)
      articleSecurityGroups = article.security_groups.reduce((x, y) => x + y);

    if (!permissions.userCanAny(articleSecurityGroups, this.isArticleOwner))
      this.$router.push({ name: "error-403" });

    if (article.feature_image) {
      article.feature_image.small
        ? this.loadImageUrl(article.feature_image.small, "small")
        : this.loadImageUrl(article.feature_image.original, "small");
      article.feature_image.medium
        ? this.loadImageUrl(article.feature_image.medium, "medium")
        : this.loadImageUrl(article.feature_image.original, "medium");
      article.feature_image.large
        ? this.loadImageUrl(article.feature_image.large, "large")
        : this.loadImageUrl(article.feature_image.original, "large");
      this.articleFeatureImage.original = this.loadImageUrl(
        article.feature_image.original,
        "original"
      );
    }

    if (this.author && this.author.avatar) {
      this.authorImage = await this.loadImageUrl(
        this.author.avatar.small,
        "author"
      );
    }

    if (this.updated_by && this.updated_by.avatar) {
      this.updatedByImage = await this.loadImageUrl(
        this.updated_by.avatar.small,
        "author"
      );
    }

    const readByContacts = this.readByContacts.filter((c) => c.id && c.avatar);

    for (let i = 0; i < readByContacts.length; i++) {
      if (readByContacts[i] && readByContacts[i].avatar) {
        this.$set(
          this.contactImageUrls,
          i,
          await this.loadImageUrl(readByContacts[i].avatar.original, "author")
        );
      }
    }
  },
  async mounted() {
    this.$nextTick(function() {
      this.addReadBy(this.getCurrentContact.id);
    });
  },

  computed: {
    ...mapGetters([
      "getArticleById",
      "getCurrentContact",
      "getContactByUserId",
      "getArticleCategoryById",
      "getContactById",
    ]),
    article: function() {
      if (this.previewMode) {
        return this.$store.getters["editArticle/getArticle"];
      }
      let article = this.getArticleById(this.articleId);
      return article ? article : new Article();
    },
    currentContact: function() {
      return this.getCurrentContact;
    },
    readByContacts: function() {
      const that = this;
      const readByContacts = this.article.read_by_contacts
        .map((cId) => that.getContactById(cId))
        .filter((c) => c !== undefined);
      for (const con of readByContacts) {
        if (!con) continue;

        con.firstLetter = con.first_name?.length > 0 ? con.first_name[0] : "T";
        con.secondLetter = con.last_name?.length > 0 ? con.last_name[0] : "T";
      }
      return readByContacts;
    },
  },
  methods: {
    ...mapActions(["updateArticle", "deleteArticle"]),
    editArticle: function() {
      this.$router.push({
        name: "article-add",
        params: { articleId: Number(this.articleId) },
      });
    },

    async loadImageUrl(imagePath, type) {
      try {
        const token = JSON.parse(localStorage.getItem("user")).jwt;
        const response = await axios.get(imagePath, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        });

        if (type === "author") {
          const imageUrl = URL.createObjectURL(response.data);
          return imageUrl;
        }

        const imageUrl = URL.createObjectURL(response.data);

        // Use Vue.set or $set to ensure reactivity
        this.$set(this.articleFeatureImage, type, imageUrl);
      } catch (error) {
        console.error("Failed to load image", error);
      }
    },
    formatDate: function(date) {
      return moment(date).format("YYYY-MM-DD");
    },
    setFavorite: function() {
      this.isFavorite = true;
      this.article.favorite = true;
      this.updateArticle(this.article);
    },
    unsetFavorite: function() {
      this.isFavorite = false;
      this.article.favorite = false;
      this.updateArticle(this.article);
    },
    deleteArticleAlert: function() {
      this.$swal({
        title: "Vill du verkligen radera artikeln?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Radera artikel",
        cancelButtonText: "Avbryt",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          const sectionsWithVideos = this.article.sections.filter((s) =>
            s.contents.find((c) => c.video)
          );
          sectionsWithVideos.forEach((section) => {
            const videos = section.contents
              .filter((c) => c.video?.url1 !== "NIL")
              .map((c) => c.video);
            videos.forEach((video) => {
              this.$store.dispatch("deleteVideo", video);
            });
          });

          this.deleteArticle(this.article);
          this.$router.push({ name: "search-articles" });
        }
      });
    },
    addReadBy: function(contactId) {
      const readBy = [...this.article.read_by_contacts];
      if (readBy.includes(contactId)) {
        return;
      }

      this.article.read_by_contacts.push(contactId);
      this.read_by_contacts = this.article.read_by_contacts;
      this.updateArticle(this.article);
    },
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  components: {
    Section,
    BRow,
    BCol,
    BCard,
    BCollapse,
    BCardText,
    BButton,
    BAlert,
    BAvatarGroup,
    BAvatar,
    //BTabs,
    //BTab,
    //BBadge,
    //BForm,
    //BFormGroup,
    //BFormSelect,
    //BFormTextarea,
    //AppTimeline,
    //AppTimelineItem,
  },
};
</script>

<style scoped>
.backdrop {
  background-repeat: no-repeat;
  background-size: cover;
}
figure {
  backdrop-filter: blur(8px) brightness(0.6);
}
picture {
  text-align: center;
  width: 100%;
}
picture img {
  max-width: 100%;
  max-height: 500px;
  width: auto;
}
.article-actions {
  position: relative;
  top: auto;
}
@media screen and (min-width: 992px) {
  .article-actions {
    position: sticky;
    top: 92px;
  }
}
@media screen and (min-width: 1200px) {
  .article-actions {
    position: -webkit-sticky;
    position: sticky;
    top: 112px;
  }
}
</style>
